// =============================================================================
// Admin Investor Import Page
// =============================================================================
// Bulk import investors from Excel file
// =============================================================================

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../components/ui';
import { adminService } from '../../services/admin.service';
import { getApiError } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Types
// =============================================================================

interface ImportResults {
  created: { users: number; profiles: number; investments: number; contributions: number };
  skipped: { rowNumber: number; email: string; reason: string }[];
  warnings: { rowNumber: number; email: string; warning: string }[];
  errors: { rowNumber: number; email: string; error: string }[];
}

// =============================================================================
// Component
// =============================================================================

export function InvestorImportPage() {
  const navigate = useNavigate();
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('investors', 'create');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ImportResults | null>(null);

  // Handle file selection
  const handleFileSelect = useCallback((selectedFile: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResults(null);
  }, []);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  }, [handleFileSelect]);

  // Handle import
  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const importResults = await adminService.importInvestors(file);
      setResults(importResults);
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsUploading(false);
    }
  };

  // Reset for another import
  const handleReset = () => {
    setFile(null);
    setResults(null);
    setError(null);
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      await adminService.downloadImportTemplate();
    } catch (err) {
      setError('Failed to download template. Please try again.');
    }
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('investors', 'create')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin/investors"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Investors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Bulk import investor profiles from an Excel file
          </p>
        </div>
      </div>

      {/* Results View */}
      {results ? (
        <div className="space-y-6">
          {/* Summary Card */}
          <Card padding="lg">
            <div className="text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
              <h2 className="mt-4 text-xl font-semibold text-gray-900">Import Complete</h2>
              <p className="mt-2 text-gray-600">
                Your investor data has been processed successfully.
              </p>
            </div>

            {/* Stats Grid */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{results.created.users}</p>
                <p className="text-sm text-green-600">New Users</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{results.created.profiles}</p>
                <p className="text-sm text-green-600">Profiles Created</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{results.created.investments}</p>
                <p className="text-sm text-blue-600">Investments</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{results.created.contributions}</p>
                <p className="text-sm text-blue-600">Contributions</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">{results.skipped.length}</p>
                <p className="text-sm text-yellow-600">Skipped</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{results.errors.length}</p>
                <p className="text-sm text-red-600">Errors</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-center gap-4">
              <Button
                variant="secondary"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Import More
              </Button>
              <Button
                onClick={() => navigate('/admin/investors')}
                className="flex items-center gap-2"
              >
                View Investors
              </Button>
            </div>
          </Card>

          {/* Warnings */}
          {results.warnings.length > 0 && (
            <Card padding="md">
              <div className="flex items-center gap-2 mb-4">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                <h3 className="font-semibold text-gray-900">Warnings ({results.warnings.length})</h3>
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-yellow-800">Row</th>
                      <th className="px-3 py-2 text-left font-medium text-yellow-800">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-yellow-800">Warning</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-yellow-100">
                    {results.warnings.map((w, i) => (
                      <tr key={i} className="bg-yellow-50/50">
                        <td className="px-3 py-2 text-yellow-700">{w.rowNumber}</td>
                        <td className="px-3 py-2 text-yellow-700">{w.email}</td>
                        <td className="px-3 py-2 text-yellow-700">{w.warning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Skipped */}
          {results.skipped.length > 0 && (
            <Card padding="md">
              <div className="flex items-center gap-2 mb-4">
                <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />
                <h3 className="font-semibold text-gray-900">Skipped ({results.skipped.length})</h3>
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Row</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {results.skipped.map((s, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 text-gray-600">{s.rowNumber}</td>
                        <td className="px-3 py-2 text-gray-600">{s.email}</td>
                        <td className="px-3 py-2 text-gray-600">{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Errors */}
          {results.errors.length > 0 && (
            <Card padding="md">
              <div className="flex items-center gap-2 mb-4">
                <XCircleIcon className="h-5 w-5 text-red-500" />
                <h3 className="font-semibold text-gray-900">Errors ({results.errors.length})</h3>
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-red-800">Row</th>
                      <th className="px-3 py-2 text-left font-medium text-red-800">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-red-800">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {results.errors.map((e, i) => (
                      <tr key={i} className="bg-red-50/50">
                        <td className="px-3 py-2 text-red-700">{e.rowNumber}</td>
                        <td className="px-3 py-2 text-red-700">{e.email}</td>
                        <td className="px-3 py-2 text-red-700">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <>
          {/* Step 1: Download Template */}
          <Card padding="lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-700">1</span>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Download Template</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Download the Excel template, fill in your investor data, then upload it below.
                </p>
                <Button
                  variant="secondary"
                  className="mt-4 flex items-center gap-2"
                  onClick={handleDownloadTemplate}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download Excel Template
                </Button>
                <p className="mt-2 text-xs text-gray-500">
                  The template includes instructions and example data.
                </p>
              </div>
            </div>
          </Card>

          {/* Step 2: Upload File */}
          <Card padding="lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-700">2</span>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Upload Completed Template</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Upload your completed Excel file to import investor profiles.
                </p>

                {/* Error Message */}
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Drop Zone */}
                <label
                  className={`mt-4 border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer block ${
                    isDragging
                      ? 'border-primary-500 bg-primary-50'
                      : file
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleInputChange}
                    className="hidden"
                  />
                  {file ? (
                    <div className="flex flex-col items-center">
                      <CheckCircleIcon className="h-12 w-12 text-green-500" />
                      <p className="mt-2 text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setFile(null); }}
                        className="mt-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <>
                      <DocumentArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto" />
                      <p className="mt-2 text-sm text-gray-600">
                        Drag and drop your Excel file here, or{' '}
                        <span className="text-primary-600 hover:text-primary-700">
                          browse
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        Supports .xlsx and .xls files
                      </p>
                    </>
                  )}
                </label>

                {/* Import Button */}
                {file && (
                  <div className="mt-4 flex justify-end gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => setFile(null)}
                      disabled={isUploading}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleImport}
                      disabled={isUploading || !canCreate}
                      className="flex items-center gap-2"
                    >
                      {isUploading ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <DocumentArrowUpIcon className="h-4 w-4" />
                          Import Investors
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card padding="md">
            <h3 className="font-semibold text-gray-900 mb-2">Import Notes</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>Email</strong> is the only required field. All other fields are optional.</li>
              <li>• Same email with different profile types will create multiple profiles under one user.</li>
              <li>• Duplicate profiles (same email + profile type + name) will be skipped.</li>
              <li>• No welcome emails will be sent. Investors can activate their accounts via the login page.</li>
              <li>• Sensitive data (SSN, FEIN, bank info) will be encrypted.</li>
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}

export default InvestorImportPage;
